/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { customElement, html } from 'lit-element';
import { BasePersonCardSection } from '../BasePersonCardSection';
import { getSvg, SvgIcon } from '../../../../utils/SvgHelper';
import { getRelativeDisplayDate } from '../../../../utils/Utils';
import { styles } from './mgt-person-card-messages-css';
import { strings } from './strings';
/**
 * The email messages subsection of the person card
 *
 * @export
 * @class MgtPersonCardMessages
 * @extends {MgtTemplatedComponent}
 */
let MgtPersonCardMessages = class MgtPersonCardMessages extends BasePersonCardSection {
    /**
     * Array of styles to apply to the element. The styles should be defined
     * using the `css` tag function.
     */
    static get styles() {
        return styles;
    }
    get strings() {
        return strings;
    }
    constructor(messages) {
        super();
        this._messages = messages;
    }
    /**
     * The name for display in the overview section.
     *
     * @readonly
     * @type {string}
     * @memberof MgtPersonCardMessages
     */
    get displayName() {
        return this.strings.emailsSectionTitle;
    }
    /**
     * Reset any state in the section
     *
     * @protected
     * @memberof MgtPersonCardMessages
     */
    clearState() {
        super.clearState();
        this._messages = [];
    }
    /**
     * Render the icon for display in the navigation ribbon.
     *
     * @returns {TemplateResult}
     * @memberof MgtPersonCardMessages
     */
    renderIcon() {
        return getSvg(SvgIcon.Messages);
    }
    /**
     * Render the compact view
     *
     * @returns {TemplateResult}
     * @memberof MgtPersonCardMessages
     */
    renderCompactView() {
        let contentTemplate;
        if (this.isLoadingState) {
            contentTemplate = this.renderLoading();
        }
        else if (!this._messages || !this._messages.length) {
            contentTemplate = this.renderNoData();
        }
        else {
            const messageTemplates = this._messages
                ? this._messages.slice(0, 3).map(message => this.renderMessage(message))
                : [];
            contentTemplate = html `
        ${messageTemplates}
      `;
        }
        return html `
      <div class="root compact">
        ${contentTemplate}
      </div>
    `;
    }
    /**
     * Render the full view
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPersonCardMessages
     */
    renderFullView() {
        let contentTemplate;
        if (this.isLoadingState) {
            contentTemplate = this.renderLoading();
        }
        else if (!this._messages || !this._messages.length) {
            contentTemplate = this.renderNoData();
        }
        else {
            contentTemplate = html `
        ${this._messages.slice(0, 5).map(message => this.renderMessage(message))}
      `;
        }
        return html `
      <div class="root">
        <div class="title" tabindex="0">${this.strings.emailsSectionTitle}</div>
        ${contentTemplate}
      </div>
    `;
    }
    /**
     * Render a message item
     *
     * @protected
     * @param {IMessage} message
     * @returns {TemplateResult}
     * @memberof MgtPersonCardMessages
     */
    renderMessage(message) {
        return html `
      <div class="message" @click=${() => this.handleMessageClick(message)}>
        <div class="message__detail">
          <div class="message__subject">${message.subject}</div>
          <div class="message__from">${message.from.emailAddress.name}</div>
          <div class="message__message">${message.bodyPreview}</div>
        </div>
        <div class="message__date">${getRelativeDisplayDate(new Date(message.receivedDateTime))}</div>
      </div>
    `;
    }
    handleMessageClick(message) {
        window.open(message.webLink, '_blank', 'noreferrer');
    }
};
MgtPersonCardMessages = __decorate([
    customElement('mgt-person-card-messages'),
    __metadata("design:paramtypes", [Array])
], MgtPersonCardMessages);
export { MgtPersonCardMessages };
//# sourceMappingURL=mgt-person-card-messages.js.map